import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CalendarRange } from "lucide-react";

export const ProfileTab = () => {
  const { user, refreshUser, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [weekly, setWeekly] = useState(user?.weekly_snapshot !== false);
  const [savingWeekly, setSavingWeekly] = useState(false);
  const isOwner = user?.role === "owner";

  const toggleWeekly = async (v) => {
    setWeekly(v);
    setSavingWeekly(true);
    try {
      await api.patch("/me", { weekly_snapshot: v });
      setUser((u) => (u ? { ...u, weekly_snapshot: v } : u));
      toast.success(v ? "You'll get the Monday snapshot." : "Monday snapshot turned off.");
    } catch (err) {
      setWeekly(!v);
      toast.error(errMsg(err));
    } finally {
      setSavingWeekly(false);
    }
  };

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await api.patch("/me", { name });
      toast.success("Profile updated.");
      refreshUser();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/me/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {isOwner && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-muted-foreground" /> Email preferences
            </CardTitle>
            <CardDescription>Choose which summary emails land in your inbox.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4 rounded-xl border p-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">Monday weekly snapshot</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  A start-of-week overview with your totals and everything due in the next 7 days. Owners only.
                </p>
              </div>
              <Switch checked={weekly} onCheckedChange={toggleWeekly} disabled={savingWeekly}
                data-testid="profile-weekly-snapshot-toggle" />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile</CardTitle>
          <CardDescription>Signed in as {user?.email} · <span className="capitalize">{user?.role}</span> of {user?.org?.name}.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveName} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile-name">Your name</Label>
              <Input id="profile-name" required maxLength={100} value={name} onChange={(e) => setName(e.target.value)}
                data-testid="profile-name-input" />
            </div>
            <Button type="submit" disabled={savingName} className="rounded-xl" data-testid="profile-name-save-button">
              {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Change password</CardTitle>
          <CardDescription>Use at least 8 characters. You'll stay logged in on this device.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={savePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pw-current">Current password</Label>
              <Input id="pw-current" type="password" autoComplete="current-password" required value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="password-current-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pw-next">New password</Label>
                <Input id="pw-next" type="password" autoComplete="new-password" required minLength={8} maxLength={72}
                  value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="password-new-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw-confirm">Confirm new password</Label>
                <Input id="pw-confirm" type="password" autoComplete="new-password" required minLength={8} maxLength={72}
                  value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="password-confirm-input" />
              </div>
            </div>
            <Button type="submit" disabled={savingPw} className="rounded-xl" data-testid="password-save-button">
              {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
